import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Bot, Check, ChevronDown, DollarSign, Loader2, Maximize2, Minus, Package, Send, Sparkles, Trash2, Wand2, X } from 'lucide-react';
import {
  suggestPricingAdvice,
  type AppScreensChatMessagePayload,
  type PricingAdviceProductPayload,
  type PricingAdviceSuggestion,
} from '../api/client';
import MarkdownMessage from './MarkdownMessage';

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  /** Machine-applicable price changes attached to an assistant reply (empty when none). */
  suggestions?: PricingAdviceSuggestion[];
}

/** Live (unsaved) pricing form state forwarded to the advisor. */
export interface PricingAdvicePayload {
  currency?: string;
  appPriceTier?: string;
  products: PricingAdviceProductPayload[];
}

export interface PricingAIChatPanelProps {
  open: boolean;
  onClose: () => void;
  appId: string;
  /** "ios" | "android" — the currently selected store platform. */
  platform: string;
  /** Live pricing form state — forwarded so the advisor sees current, unsaved prices. */
  pricing: PricingAdvicePayload;
  /**
   * When provided, assistant replies that carry structured price suggestions show an
   * "Apply" action that calls this with those suggestions (applied to the live pricing
   * form: existing productId → update, otherwise → create).
   */
  onApplySuggestion?: (suggestions: PricingAdviceSuggestion[]) => void;
}

/**
 * Right-side drawer chat assistant for the Pricing page. The assistant is grounded in the
 * user's current (live) active-on-sale prices and gives tier-based pricing suggestions.
 * Conversation is client-only (not persisted). Suggestions are text-only in phase 1.
 */
const PricingAIChatPanel: React.FC<PricingAIChatPanelProps> = ({
  open,
  onClose,
  appId,
  platform,
  pricing,
  onApplySuggestion,
}) => {
  const { t, i18n } = useTranslation();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [minimized, setMinimized] = useState(false);
  // Packages (products) the user wants the assistant to act on. Actions are scoped to these.
  const [selectedProductIds, setSelectedProductIds] = useState<string[]>([]);
  const [packageMenuOpen, setPackageMenuOpen] = useState(false);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  const packageMenuRef = useRef<HTMLDivElement | null>(null);

  // Default the package selection to the active-on-sale products, and reset it whenever
  // the store platform changes (its product set changes with it).
  useEffect(() => {
    setSelectedProductIds(pricing.products.filter(p => p.activeOnSale).map(p => p.productId));
    // Intentionally keyed on platform only: preserve the user's manual selection across
    // unrelated form edits while the panel stays on the same platform.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [platform]);

  // Keep the latest live pricing + selection in a ref so the send handler always ships them.
  const pricingRef = useRef({ platform, pricing, selectedProductIds });
  useEffect(() => {
    pricingRef.current = { platform, pricing, selectedProductIds };
  }, [platform, pricing, selectedProductIds]);

  // Close the package menu on outside click.
  useEffect(() => {
    if (!packageMenuOpen) return undefined;
    const onDown = (e: MouseEvent) => {
      if (packageMenuRef.current && !packageMenuRef.current.contains(e.target as Node)) {
        setPackageMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [packageMenuOpen]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, loading]);

  // Signal global widgets (e.g. FeedbackWidget) to step aside while the chat drawer is open,
  // so the assistant is not covered by the bottom-right feedback button.
  useEffect(() => {
    window.dispatchEvent(new CustomEvent('appscreens:chat-open', { detail: open }));
    return () => {
      window.dispatchEvent(new CustomEvent('appscreens:chat-open', { detail: false }));
    };
  }, [open]);

  useEffect(() => {
    if (open) {
      const id = window.setTimeout(() => inputRef.current?.focus(), 50);
      return () => window.clearTimeout(id);
    }
    return undefined;
  }, [open]);

  const allProducts = pricing.products;
  const selectedCount = selectedProductIds.length;

  const toggleProduct = useCallback((productId: string) => {
    setSelectedProductIds(prev =>
      prev.includes(productId) ? prev.filter(id => id !== productId) : [...prev, productId]
    );
  }, []);

  const send = useCallback(
    async (text: string) => {
      const message = text.trim();
      if (!message || loading || !appId) return;

      const history: AppScreensChatMessagePayload[] = messages.map(m => ({ role: m.role, content: m.content }));

      setMessages(prev => [...prev, { role: 'user', content: message }]);
      setInput('');
      setLoading(true);
      try {
        const ctx = pricingRef.current;
        // Scope the request to the packages the user picked: send only those products and
        // mark them active so the backend evaluates exactly this set.
        const chosen = new Set(ctx.selectedProductIds);
        const scopedProducts = ctx.pricing.products
          .filter(p => chosen.has(p.productId))
          .map(p => ({ ...p, activeOnSale: true }));
        const { reply, suggestions } = await suggestPricingAdvice(appId, {
          message,
          messages: history,
          platform: ctx.platform,
          responseLanguage: i18n.language || undefined,
          pricing: {
            currency: ctx.pricing.currency,
            appPriceTier: ctx.pricing.appPriceTier,
            products: scopedProducts,
          },
        });
        setMessages(prev => [
          ...prev,
          { role: 'assistant', content: reply || t('pricing.aiAdvisorError'), suggestions },
        ]);
      } catch {
        setMessages(prev => [...prev, { role: 'assistant', content: t('pricing.aiAdvisorError') }]);
      } finally {
        setLoading(false);
        window.setTimeout(() => inputRef.current?.focus(), 30);
      }
    },
    [appId, loading, messages, t, i18n]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        void send(input);
      }
    },
    [input, send]
  );

  if (!open) return null;

  const hasMessages = messages.length > 0;

  // Minimized: collapse to a compact floating pill at bottom-right.
  if (minimized) {
    return (
      <div className="fixed bottom-4 right-4 z-40 flex items-center gap-2 rounded-full border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-lg pl-3 pr-2 py-2">
        <span className="flex h-7 w-7 items-center justify-center rounded-full bg-primary-100 dark:bg-primary-900/40 text-primary-600 dark:text-primary-300">
          <Bot className="h-4 w-4" />
        </span>
        <button
          type="button"
          onClick={() => setMinimized(false)}
          className="text-sm font-medium text-gray-700 dark:text-gray-200 hover:text-primary-600 dark:hover:text-primary-300"
        >
          {t('pricing.aiAdvisorTitle')}
        </button>
        {loading && <Loader2 className="h-4 w-4 animate-spin text-primary-500" />}
        <button
          type="button"
          onClick={() => setMinimized(false)}
          title={t('pricing.aiAdvisorExpand')}
          className="inline-flex h-7 w-7 items-center justify-center rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-800 dark:hover:text-gray-200"
        >
          <Maximize2 className="h-3.5 w-3.5" />
        </button>
        <button
          type="button"
          onClick={onClose}
          title={t('common.close')}
          className="inline-flex h-7 w-7 items-center justify-center rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-800 dark:hover:text-gray-200"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
    );
  }

  return (
    // Docked, non-modal drawer: no backdrop so the pricing form behind it stays interactive.
    <aside className="fixed top-14 right-0 bottom-0 z-40 flex w-full max-w-md flex-col bg-white dark:bg-gray-900 shadow-2xl border-l border-t border-gray-200 dark:border-gray-700">
      {/* Header */}
      <div className="flex shrink-0 items-start gap-3 border-b border-gray-200 dark:border-gray-700 p-4">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary-100 dark:bg-primary-900/40 text-primary-600 dark:text-primary-300">
          <DollarSign className="h-5 w-5" />
        </div>
        <div className="min-w-0 flex-1">
          <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
            {t('pricing.aiAdvisorTitle')}
          </h2>
          <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
            {t('pricing.aiAdvisorDescription')}
          </p>
        </div>
        <div className="flex items-center gap-1">
          {hasMessages && (
            <button
              type="button"
              onClick={() => setMessages([])}
              title={t('pricing.aiAdvisorClear')}
              className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-800 dark:hover:text-gray-200"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          )}
          <button
            type="button"
            onClick={() => setMinimized(true)}
            title={t('pricing.aiAdvisorMinimize')}
            className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-800 dark:hover:text-gray-200"
          >
            <Minus className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={onClose}
            title={t('common.close')}
            className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-800 dark:hover:text-gray-200"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Messages */}
      <div ref={scrollRef} className="flex-1 min-h-0 overflow-y-auto p-4 space-y-4">
        {!hasMessages && !loading && (
          <div className="flex flex-col items-center justify-center gap-3 py-10 text-center">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary-50 dark:bg-primary-900/30 text-primary-500 dark:text-primary-300">
              <Sparkles className="h-6 w-6" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-700 dark:text-gray-200">
                {t('pricing.aiAdvisorEmpty')}
              </p>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                {selectedCount > 0
                  ? t('pricing.aiAdvisorEmptyHint', { count: selectedCount })
                  : t('pricing.aiAdvisorNoActiveHint')}
              </p>
            </div>
            <div className="mt-1 flex flex-col gap-2 w-full max-w-xs">
              <button
                type="button"
                onClick={() => void send(t('pricing.aiAdvisorQuickEvaluate'))}
                className="inline-flex items-center gap-2 rounded-lg border border-gray-200 dark:border-gray-700 px-3 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800"
              >
                <Wand2 className="h-4 w-4 text-primary-500 shrink-0" />
                {t('pricing.aiAdvisorQuickEvaluate')}
              </button>
              <button
                type="button"
                onClick={() => void send(t('pricing.aiAdvisorQuickTiers'))}
                className="inline-flex items-center gap-2 rounded-lg border border-gray-200 dark:border-gray-700 px-3 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800"
              >
                <DollarSign className="h-4 w-4 text-primary-500 shrink-0" />
                {t('pricing.aiAdvisorQuickTiers')}
              </button>
            </div>
          </div>
        )}

        {messages.map((m, i) => (
          <div key={i} className={m.role === 'user' ? 'flex justify-end' : 'flex justify-start'}>
            <div className={m.role === 'user' ? 'max-w-[85%]' : 'max-w-[92%] w-full'}>
              <div className="mb-1 text-[11px] font-medium text-gray-400 dark:text-gray-500 px-1">
                {m.role === 'user' ? t('pricing.aiAdvisorYou') : t('pricing.aiAdvisorAssistant')}
              </div>
              {m.role === 'user' ? (
                <div className="rounded-2xl rounded-tr-sm bg-primary-600 px-3.5 py-2.5 text-sm text-white whitespace-pre-wrap break-words">
                  {m.content}
                </div>
              ) : (
                <div className="rounded-2xl rounded-tl-sm bg-gray-100 dark:bg-gray-800 px-3.5 py-2.5 text-sm text-gray-800 dark:text-gray-100 break-words">
                  <MarkdownMessage content={m.content} />
                </div>
              )}
              {m.role === 'assistant' && (m.suggestions?.length ?? 0) > 0 && (
                <div className="mt-1.5 px-1">
                  <button
                    type="button"
                    onClick={
                      onApplySuggestion && m.suggestions
                        ? () => onApplySuggestion(m.suggestions ?? [])
                        : undefined
                    }
                    disabled={!onApplySuggestion}
                    title={onApplySuggestion ? undefined : t('pricing.aiAdvisorApplyComingSoon')}
                    className="inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs font-medium text-primary-600 dark:text-primary-300 hover:bg-primary-50 dark:hover:bg-primary-900/30 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                  >
                    <Wand2 className="h-3.5 w-3.5" />
                    {t('pricing.aiAdvisorApply')}
                  </button>
                </div>
              )}
            </div>
          </div>
        ))}

        {loading && (
          <div className="flex justify-start">
            <div className="inline-flex items-center gap-2 rounded-2xl rounded-tl-sm bg-gray-100 dark:bg-gray-800 px-3.5 py-2.5 text-sm text-gray-500 dark:text-gray-400">
              <Loader2 className="h-4 w-4 animate-spin" />
              {t('pricing.aiAdvisorThinking')}
            </div>
          </div>
        )}
      </div>

      {/* Composer */}
      <div className="shrink-0 border-t border-gray-200 dark:border-gray-700 p-3 space-y-2">
        {allProducts.length > 0 && (
          <div className="relative" ref={packageMenuRef}>
            <button
              type="button"
              onClick={() => setPackageMenuOpen(o => !o)}
              className="inline-flex w-full items-center gap-1.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/60 px-2.5 py-1.5 text-xs font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
            >
              <Package className="h-3.5 w-3.5 shrink-0 text-primary-500" />
              <span className="truncate">
                {selectedCount === 0
                  ? t('pricing.aiAdvisorNoPackagesSelected')
                  : t('pricing.aiAdvisorPackagesSelected', { count: selectedCount, total: allProducts.length })}
              </span>
              <ChevronDown className={`ml-auto h-3.5 w-3.5 shrink-0 transition-transform ${packageMenuOpen ? 'rotate-180' : ''}`} />
            </button>
            {packageMenuOpen && (
              <div className="absolute bottom-full left-0 right-0 mb-1 z-10 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-xl">
                <div className="flex items-center justify-between gap-2 border-b border-gray-100 dark:border-gray-800 px-2.5 py-1.5">
                  <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">
                    {t('pricing.aiAdvisorPackages')}
                  </span>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setSelectedProductIds(allProducts.map(p => p.productId))}
                      className="text-[11px] font-medium text-primary-600 dark:text-primary-400 hover:underline"
                    >
                      {t('pricing.aiAdvisorSelectAll')}
                    </button>
                    <button
                      type="button"
                      onClick={() => setSelectedProductIds([])}
                      className="text-[11px] font-medium text-gray-500 dark:text-gray-400 hover:underline"
                    >
                      {t('pricing.aiAdvisorClearAll')}
                    </button>
                  </div>
                </div>
                <div className="max-h-56 overflow-y-auto py-1">
                  {allProducts.map(p => {
                    const checked = selectedProductIds.includes(p.productId);
                    return (
                      <button
                        key={p.productId}
                        type="button"
                        onClick={() => toggleProduct(p.productId)}
                        className="flex w-full items-center gap-2 px-2.5 py-1.5 text-left hover:bg-gray-50 dark:hover:bg-gray-800"
                      >
                        <span
                          className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border ${
                            checked
                              ? 'border-primary-500 bg-primary-500 text-white'
                              : 'border-gray-300 dark:border-gray-600'
                          }`}
                        >
                          {checked && <Check className="h-3 w-3" />}
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-sm text-gray-800 dark:text-gray-100">
                            {p.name?.trim() || p.productId}
                          </span>
                          <span className="block truncate text-[11px] text-gray-400 dark:text-gray-500">
                            <span className="font-mono">{p.productId}</span>
                            {' · '}
                            {p.type === 'subscription'
                              ? t('appStoreSettings.subscription')
                              : t('appStoreSettings.oneTime')}
                            {p.activeOnSale ? ` · ${t('pricing.activeOnSale')}` : ''}
                          </span>
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}
        <div className="flex items-end gap-2">
          <textarea
            ref={inputRef}
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={t('pricing.aiAdvisorPlaceholder')}
            rows={1}
            className="flex-1 resize-none rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3.5 py-2.5 text-sm text-gray-800 dark:text-gray-100 focus:border-primary-400 focus:outline-none focus:ring-1 focus:ring-primary-400 max-h-32"
          />
          <button
            type="button"
            onClick={() => void send(input)}
            disabled={!input.trim() || loading}
            title={t('pricing.aiAdvisorSend')}
            className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-40 disabled:pointer-events-none"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          </button>
        </div>
      </div>
    </aside>
  );
};

export default PricingAIChatPanel;
